import { useEffect, useState, type JSX } from 'react';
import './components.css';

export interface NumberFieldProps {
  value: number;
  onChange(value: number): void;
  onCommit?(): void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  label: string;
  id?: string;
  disabled?: boolean;
  /** Decimal places shown when not being edited. */
  precision?: number;
  /**
   * 'change' (default) reports every valid keystroke; 'commit' reports only when
   * the field is left, Enter is pressed or the arrow keys step the value.
   */
  applyOn?: 'change' | 'commit';
}

/** Parses free text into a finite number inside [min, max]; null when not a number. */
export function parseNumberInput(text: string, min?: number, max?: number): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  let out = value;
  if (min !== undefined && out < min) out = min;
  if (max !== undefined && out > max) out = max;
  return out;
}

export function formatNumber(value: number, precision = 3): string {
  if (!Number.isFinite(value)) return '';
  return String(Number(value.toFixed(precision)));
}

/**
 * Text-backed numeric input: the draft is free-form while typing and only
 * valid numbers are pushed out, so half-typed values such as "-" never reach
 * the document.
 */
export function NumberField(props: NumberFieldProps): JSX.Element {
  const { value, onChange, onCommit, min, max, step = 1, unit, label, id, disabled, precision, applyOn = 'change' } = props;
  const [draft, setDraft] = useState<string | null>(null);

  // An outside change replaces the draft; the echo of what is being typed (possibly clamped) must not,
  // or typing "1920" into a field with a minimum would be rewritten after the first digit.
  useEffect(() => {
    setDraft((current) => (current !== null && parseNumberInput(current, min, max) === value ? current : null));
  }, [value, min, max]);

  const text = draft ?? formatNumber(value, precision);
  const invalid = draft !== null && parseNumberInput(draft, min, max) === null;

  const push = (raw: string): void => {
    setDraft(raw);
    if (applyOn === 'commit') return;
    const parsed = parseNumberInput(raw, min, max);
    if (parsed !== null && parsed !== value) onChange(parsed);
  };

  const finish = (): void => {
    if (applyOn === 'commit' && draft !== null) {
      const parsed = parseNumberInput(draft, min, max);
      if (parsed !== null && parsed !== value) onChange(parsed);
    }
    setDraft(null);
    onCommit?.();
  };

  const bump = (direction: 1 | -1, multiplier: number): void => {
    const next = parseNumberInput(String(Number((value + direction * step * multiplier).toFixed(6))), min, max);
    if (next !== null) {
      setDraft(null);
      onChange(next);
    }
  };

  return (
    <span className="ui-number">
      <input
        id={id}
        className={invalid ? 'ui-input invalid' : 'ui-input'}
        type="text"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        value={text}
        onChange={(e) => push(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            bump(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey ? 10 : 1);
          } else if (e.key === 'Enter') finish();
        }}
      />
      {unit ? <span className="ui-dim">{unit}</span> : null}
    </span>
  );
}
